import { useCallback, useEffect, useState } from "react";

import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  detectPushSupport,
  getPushConfig,
  sendTestPush,
  subscribePush,
  unsubscribePush,
  urlBase64ToUint8Array,
} from "@/api/push";

/**
 * Opt-in card for Web Push.
 *
 * The subscription lives in the browser, so the source of truth is the
 * service worker registration — not our server. We read it on mount and after
 * every change rather than tracking it in app state.
 */
export function PushNotificationsPanel() {
  const { t } = useTranslation();
  const support = detectPushSupport();

  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const config = useQuery({
    queryKey: ["push-config"],
    queryFn: getPushConfig,
    enabled: support.supported,
    retry: false,
  });

  const refreshState = useCallback(async () => {
    if (!support.supported) return;
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    setSubscribed(existing !== null);
  }, [support.supported]);

  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  const enable = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setError(t("push.permissionDenied"));
        return;
      }
      const key = config.data?.public_key;
      if (!key) {
        setError(t("push.serverNotConfigured"));
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        }));
      await subscribePush(sub);
      setSubscribed(true);
      setInfo(t("push.enabledOk"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush(sub.endpoint);
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const r = await sendTestPush();
      setInfo(t("push.testSent", { count: r.delivered }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {subscribed ? <NotificationsActiveIcon color="primary" /> : <NotificationsOffIcon />}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t("push.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("push.description")}
      </Typography>

      {!support.supported && support.reason === "ios-needs-install" && (
        <Alert severity="info">{t("push.iosNeedsInstall")}</Alert>
      )}
      {!support.supported && support.reason !== "ios-needs-install" && (
        <Alert severity="warning">{t("push.unsupported")}</Alert>
      )}

      {support.supported && config.data?.enabled === false && (
        <Alert severity="warning">{t("push.serverNotConfigured")}</Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {info}
        </Alert>
      )}

      {support.supported && config.data?.enabled && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {subscribed === null || busy ? (
              <CircularProgress size={22} />
            ) : subscribed ? (
              <>
                <Button variant="outlined" color="inherit" onClick={disable}>
                  {t("push.disable")}
                </Button>
                <Button variant="text" onClick={test}>
                  {t("push.sendTest")}
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={enable}>
                {t("push.enable")}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
